<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import type { Event } from '@/types/event'

const props = defineProps<{
  modelValue: boolean
  events?: Event[]
  selectedEvent?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

type Step = 1 | 2 | 3
const currentStep = ref<Step>(1)

// Step 1 — Personal Info
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const phone = ref('')
const dob = ref('')
const sex = ref('')

// Athletic Info
const highSchool = ref('')
const gradYear = ref('')
const team = ref('')
const sport = ref('')
const position = ref('')
const heightWeight = ref('')

// Address
const address = ref('')
const city = ref('')
const state = ref('')
const zip = ref('')

// Social
const instagram = ref('')
const twitter = ref('')
const hudl = ref('')

// Event
const event = ref('')

// Add-ons
const addonScreening = ref(false)
const addonVideo = ref(false)

// Promo code
const promoCode = ref('')
const promoApplied = ref(false)
const promoError = ref('')

const BASE_PRICE = 125

const isBasketballEvent = computed(() =>
  event.value === 'shoot360-youth' || event.value === 'shoot360-14plus',
)

const promoValid = computed(
  () => promoApplied.value && promoCode.value.trim().toUpperCase() === 'SHOOT25' && isBasketballEvent.value,
)

const totalPrice = computed(() => {
  let total = BASE_PRICE
  if (addonScreening.value) total += 50
  if (addonVideo.value) total += 25
  if (promoValid.value) total -= 25
  return total
})

function applyPromo() {
  const code = promoCode.value.trim().toUpperCase()
  if (code === 'SHOOT25' && isBasketballEvent.value) {
    promoApplied.value = true
    promoError.value = ''
  } else if (code === 'SHOOT25' && !isBasketballEvent.value) {
    promoApplied.value = false
    promoError.value = 'This promo code is only valid for basketball events.'
  } else {
    promoApplied.value = false
    promoError.value = 'Invalid promo code. Please try again.'
  }
}

// Step 2 — Waiver
const waiverAccepted = ref(false)
const waiverSignature = ref('')
const waiverDate = ref('')
const waiverError = ref('')

// Step 3 — Payment
const isProcessing = ref(false)
const paymentError = ref('')

function convertDate(dateStr: string): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString(undefined, options)
}

const eventOptions = computed(() => {
  if (!props.events?.length) return []
  const now = new Date()
  return props.events
    .filter((e) => e.status === 'open' && new Date(e.date) > now)
    .map((e) => ({
      value: e.slug,
      label: `${convertDate(e.date)} — ${e.title.replace(/<br\s*\/?>/gi, ' ')}`,
    }))
})

const selectedEventLabel = computed(() => {
  const opt = eventOptions.value.find((o) => o.value === event.value)
  return opt?.label || 'M6 Elite Combine'
})

const formData = computed(() => ({
  firstName: firstName.value,
  lastName: lastName.value,
  email: email.value,
  phone: phone.value,
  dob: dob.value,
  sex: sex.value,
  highSchool: highSchool.value,
  gradYear: gradYear.value,
  team: team.value,
  sport: sport.value,
  position: position.value,
  heightWeight: heightWeight.value,
  address: address.value,
  city: city.value,
  state: state.value,
  zip: zip.value,
  instagram: instagram.value,
  twitter: twitter.value,
  hudl: hudl.value,
  event: event.value,
  waiverAccepted: waiverAccepted.value,
  waiverSignature: waiverSignature.value,
  waiverDate: waiverDate.value,
  addonScreening: addonScreening.value,
  addonVideo: addonVideo.value,
  promoCode: promoCode.value,
}))

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      resetModal()
      if (props.selectedEvent) {
        event.value = props.selectedEvent
      }
    }
  },
)

function resetModal() {
  currentStep.value = 1
  firstName.value = ''
  lastName.value = ''
  email.value = ''
  phone.value = ''
  dob.value = ''
  sex.value = ''
  highSchool.value = ''
  gradYear.value = ''
  team.value = ''
  sport.value = ''
  position.value = ''
  heightWeight.value = ''
  address.value = ''
  city.value = ''
  state.value = ''
  zip.value = ''
  instagram.value = ''
  twitter.value = ''
  hudl.value = ''
  event.value = ''
  addonScreening.value = false
  addonVideo.value = false
  promoCode.value = ''
  promoApplied.value = false
  promoError.value = ''
  waiverAccepted.value = false
  waiverSignature.value = ''
  waiverDate.value = ''
  waiverError.value = ''
  isProcessing.value = false
  paymentError.value = ''
}

function close() {
  emit('update:modelValue', false)
}

function goToWaiver() {
  waiverDate.value = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  currentStep.value = 2
}

function goBackToForm() {
  waiverError.value = ''
  currentStep.value = 1
}

function goToPayment() {
  if (!waiverAccepted.value) {
    waiverError.value = 'Please accept the waiver to continue.'
    return
  }
  if (!waiverSignature.value.trim()) {
    waiverError.value = 'Please provide your electronic signature.'
    return
  }
  waiverError.value = ''
  currentStep.value = 3
}

function goBackToWaiver() {
  paymentError.value = ''
  currentStep.value = 2
}

async function handlePayment() {
  isProcessing.value = true
  paymentError.value = ''

  try {
    const res = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData.value),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => null)
      paymentError.value = err?.error || 'Something went wrong. Please try again.'
      isProcessing.value = false
      return
    }

    const { url } = await res.json()
    window.location.href = url
  } catch {
    paymentError.value = 'Unable to connect to payment service. Please try again.'
    isProcessing.value = false
  }
}
</script>

<template>
  <BaseModal :model-value="modelValue" max-width="600px" @update:model-value="close">
    <div class="reg-body">
      <!-- Header -->
      <div class="reg-header">
        <h2>Register Now</h2>
        <p class="reg-subtitle">Secure your spot at the M6 Elite Combine</p>
        <div class="step-indicator">
          <div
            v-for="s in 3"
            :key="s"
            class="step-dot"
            :class="{ active: s <= currentStep }"
          />
        </div>
      </div>

      <!-- Step 1: Registration Form -->
      <form v-if="currentStep === 1" @submit.prevent="goToWaiver">
        <div class="form-section-label">Personal Information</div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-first-name">First Name *</label>
            <input
              id="reg-first-name"
              v-model="firstName"
              type="text"
              placeholder="First name"
              required
            />
          </div>
          <div class="form-group">
            <label for="reg-last-name">Last Name *</label>
            <input
              id="reg-last-name"
              v-model="lastName"
              type="text"
              placeholder="Last name"
              required
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-email">Email *</label>
            <input
              id="reg-email"
              v-model="email"
              type="email"
              placeholder="athlete@email.com"
              required
            />
          </div>
          <div class="form-group">
            <label for="reg-phone">Phone</label>
            <input id="reg-phone" v-model="phone" type="tel" placeholder="(555) 555-5555" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-dob">Date of Birth *</label>
            <input id="reg-dob" v-model="dob" type="date" required />
          </div>
          <div class="form-group">
            <label for="reg-sex">Sex *</label>
            <select id="reg-sex" v-model="sex" required>
              <option value="" disabled>Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>

        <div class="form-divider" />
        <div class="form-section-label">Athletic Info</div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-school">High School *</label>
            <input
              id="reg-school"
              v-model="highSchool"
              type="text"
              placeholder="School name"
              required
            />
          </div>
          <div class="form-group">
            <label for="reg-grad-year">Graduation Year *</label>
            <select id="reg-grad-year" v-model="gradYear" required>
              <option value="" disabled>Select</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
              <option value="2028">2028</option>
              <option value="2029">2029</option>
              <option value="2030">2030</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-team">Team / Club</label>
            <input id="reg-team" v-model="team" type="text" placeholder="Team or club name" />
          </div>
          <div class="form-group">
            <label for="reg-sport">Primary Sport *</label>
            <input
              id="reg-sport"
              v-model="sport"
              type="text"
              placeholder="e.g. Football, Basketball"
              required
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-position">Position</label>
            <input
              id="reg-position"
              v-model="position"
              type="text"
              placeholder="e.g. QB, PG, Forward"
            />
          </div>
          <div class="form-group">
            <label for="reg-hw">Height / Weight</label>
            <input
              id="reg-hw"
              v-model="heightWeight"
              type="text"
              placeholder="e.g. 6'1 / 185 lbs"
            />
          </div>
        </div>

        <div class="form-divider" />
        <div class="form-section-label">Address</div>
        <div class="form-row single">
          <div class="form-group">
            <label for="reg-address">Street Address</label>
            <input id="reg-address" v-model="address" type="text" placeholder="Street address" />
          </div>
        </div>
        <div class="form-row single">
          <div class="form-group">
            <label for="reg-city">City</label>
            <input id="reg-city" v-model="city" type="text" placeholder="City" />
          </div>
        </div>
        <div class="form-row">
          
          <div class="form-group">
            <label for="reg-state">State</label>
            <input id="reg-state" v-model="state" type="text" placeholder="State" />
          </div>
          <div class="form-group">
            <label for="reg-zip">Zip</label>
            <input id="reg-zip" v-model="zip" type="text" placeholder="Zip code" />
          </div>
        </div>

        <div class="form-divider" />
        <div class="form-section-label">Social & Recruiting Profiles</div>
        <div class="form-row">
          <div class="form-group">
            <label for="reg-instagram">Instagram</label>
            <input id="reg-instagram" v-model="instagram" type="text" placeholder="@handle" />
          </div>
          <div class="form-group">
            <label for="reg-twitter">Twitter / X</label>
            <input id="reg-twitter" v-model="twitter" type="text" placeholder="@handle" />
          </div>
        </div>
        <div class="form-row single">
          <div class="form-group">
            <label for="reg-hudl">Hudl Profile</label>
            <input id="reg-hudl" v-model="hudl" type="url" placeholder="https://hudl.com/..." />
          </div>
        </div>

        <div class="form-divider" />
        <div class="form-section-label">Select Combine Event *</div>
        <div class="form-row single">
          <div class="form-group">
            <select
              id="reg-event"
              v-model="event"
              :required="eventOptions.length > 0"
              :disabled="eventOptions.length === 0"
            >
              <option value="" disabled>
                {{ eventOptions.length ? 'Choose an event' : 'No events available at this time' }}
              </option>
              <option v-for="opt in eventOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <!-- Promo Code (basketball events only) -->
        <div v-if="isBasketballEvent" class="promo-section">
          <div class="form-section-label" style="margin-bottom: 8px">Promo Code</div>
          <div class="promo-row">
            <input
              v-model="promoCode"
              type="text"
              placeholder="Enter promo code"
              class="promo-input"
            />
            <button type="button" class="promo-btn" @click="applyPromo">Apply</button>
          </div>
          <p v-if="promoApplied" class="promo-success">&#10003; Promo applied! $25 discount</p>
          <p v-else-if="promoError" class="promo-error">{{ promoError }}</p>
          <p class="promo-hint">First 20 signups: use code <strong>SHOOT25</strong> for $25 off basketball events!</p>
        </div>

        <!-- Add-On Services -->
        <div class="form-section-label" style="margin-top: 24px; margin-bottom: 12px">
          Add-On Services <span class="addons-optional">(Optional)</span>
        </div>
        <div class="addons-list">
          <label class="addon-item">
            <input v-model="addonScreening" type="checkbox" />
            <div>
              <div class="addon-title">
                Comprehensive Screening by Aptiva Health
                <span class="addon-price">+$50</span>
              </div>
              <p class="addon-desc">
                Mobility &amp; Balance Evaluation, Imbalance Detection, Range of Motion Testing,
                Functional Movement Screen (FMS), Grip Strength Analysis. Performed by Certified
                Athletic Trainers &amp; Physical Therapists.
              </p>
            </div>
          </label>
          <label class="addon-item">
            <input v-model="addonVideo" type="checkbox" />
            <div>
              <div class="addon-title">
                Professional Video Footage
                <span class="addon-price">+$25</span>
              </div>
              <p class="addon-desc">
                HD video analysis of Linear Speed &amp; COD/Lateral Speed Tests. Custom feedback on
                movement efficiency available upon request.
              </p>
            </div>
          </label>
        </div>
        <button type="submit" class="modal-submit">Continue to Waiver →</button>
      </form>

      <div v-else-if="currentStep === 2" class="waiver-step">
        <div class="form-section-label">Liability Waiver & Release</div>
        <p class="waiver-intro">Please read and accept the waiver below to continue.</p>

        <div class="waiver-text">
          <p>
            <strong>WAIVER AND RELEASE OF LIABILITY</strong><br /><br />
            In consideration of being permitted to participate in the M6 Elite Combine Testing event
            ("Event") operated by MPower6 Performance LLC, I hereby acknowledge, agree, and
            represent that:<br /><br />
            <strong>1. Assumption of Risk.</strong> I understand that participation in athletic
            testing activities, including but not limited to sprinting, jumping, agility drills, and
            strength assessments, involves inherent risks of physical injury. I voluntarily assume
            all risks associated with participation, including but not limited to falls, contact
            with other participants or equipment, and effects of weather or facility conditions.<br /><br />
            <strong>2. Release of Liability.</strong> I, on behalf of myself and my heirs, assigns,
            personal representatives, and next of kin, hereby release and hold harmless MPower6
            Performance LLC, M6 Elite Combine Testing, their officers, officials, agents, employees,
            volunteers, and affiliated organizations with respect to any and all injury, disability,
            death, or loss or damage to person or property arising from my participation in the
            Event.<br /><br />
            <strong>3. Medical Authorization.</strong> I authorize event staff to seek emergency
            medical treatment on my behalf if necessary during the Event. I understand that I am
            responsible for any and all medical expenses incurred as a result of my
            participation.<br /><br />
            <strong>4. Media Release.</strong> I grant permission for photographs, video, and other
            media captured during the Event to be used for promotional, educational, and marketing
            purposes by MPower6 Performance LLC and its affiliates.<br /><br />
            <strong>5. Accuracy of Information.</strong> I certify that the registration information
            provided is accurate and complete. I understand that providing false information may
            result in disqualification.<br /><br />
            If the participant is under 18 years of age, a parent or legal guardian must accept this
            waiver on their behalf.
          </p>
        </div>

        <div class="waiver-checkbox">
          <input id="waiver-check" v-model="waiverAccepted" type="checkbox" />
          <label for="waiver-check">
            I have read, understand, and agree to the Waiver and Release of Liability. I confirm
            that I am at least 18 years old, or that my parent/legal guardian has reviewed and
            accepted this waiver on my behalf.
          </label>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="waiver-sig">Electronic Signature (Full Name) *</label>
            <input
              id="waiver-sig"
              v-model="waiverSignature"
              type="text"
              placeholder="Type your full legal name"
            />
          </div>
          <div class="form-group">
            <label>Date</label>
            <input type="text" :value="waiverDate" readonly class="readonly-input" />
          </div>
        </div>

        <p v-if="waiverError" class="form-error">{{ waiverError }}</p>

        <div class="step-buttons">
          <button type="button" class="btn-back" @click="goBackToForm">← Back</button>
          <button type="button" class="modal-submit flex-2" @click="goToPayment">
            Continue to Payment →
          </button>
        </div>
      </div>

      <!-- Step 3: Payment -->
      <div v-else-if="currentStep === 3" class="payment-step">
        <div class="payment-summary">
          <p class="payment-label">Registration Fee</p>
          <p class="payment-event">{{ selectedEventLabel }}</p>
          <p class="payment-amount">${{ totalPrice }}</p>
          <p class="payment-detail">One-time payment · Includes full combine testing</p>
        </div>

        <div class="checkout-info">
          <p>You'll be redirected to Square's secure checkout to complete payment.</p>
        </div>

        <p v-if="paymentError" class="form-error">{{ paymentError }}</p>

        <div class="step-buttons">
          <button type="button" class="btn-back" @click="goBackToWaiver">← Back</button>
          <button
            type="button"
            class="modal-submit flex-2"
            :disabled="isProcessing"
            @click="handlePayment"
          >
            {{ isProcessing ? 'Redirecting to payment...' : `Pay & Register — $${totalPrice}` }}
          </button>
        </div>

        <div class="secure-badge">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1a5 5 0 00-5 5v2a2 2 0 00-2 2v4a2 2 0 002 2h10a2 2 0 002-2v-4a2 2 0 00-2-2V6a5 5 0 00-5-5zm3 7H5V6a3 3 0 116 0v2z"
              fill="rgba(255,255,255,.3)"
            />
          </svg>
          <span>Secured by Square &middot; 256-bit encryption</span>
        </div>
      </div>

    </div>
  </BaseModal>
</template>

<style scoped>
.reg-body {
  padding: 32px;
}

.reg-header {
  text-align: center;
  margin-bottom: 24px;
}

.reg-header h2 {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 950;
  font-size: 1.8rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 4px;
}

.reg-subtitle {
  font-family: 'HND', sans-serif;
  font-size: 0.88rem;
  color: var(--text);
}

.step-indicator {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  transition: background 0.3s;
}

.step-dot.active {
  background: var(--pink-bright);
}

/* Form styles */
.form-section-label {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--white);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 14px;
}

.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.form-row.single {
  flex-direction: column;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-family: 'HND', sans-serif;
  font-size: 0.72rem;
  color: var(--pink-bright);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
  font-weight: 600;
}

.form-group input,
.form-group select {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px 14px;
  color: var(--white);
  font-family: 'HND', sans-serif;
  font-size: 0.9rem;
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  border-color: var(--pink);
  box-shadow: 0 0 0 3px rgba(208, 23, 122, 0.12);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.form-group select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23C8D0DC' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
}

.form-group select option {
  background: var(--card);
  color: var(--white);
}

.readonly-input {
  background: rgba(255, 255, 255, 0.02) !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.4) !important;
  cursor: default;
}

.form-divider {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 20px 0;
}

.modal-submit {
  width: 100%;
  padding: 16px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  font-family: 'HND', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-top: 8px;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.modal-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(208, 23, 122, 0.4);
}

.modal-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Step navigation buttons */
.step-buttons {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-back {
  flex: 1;
  padding: 14px;
  border-radius: 9999px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: var(--text);
  font-family: 'HND', sans-serif;
  font-weight: 600;
  font-size: 0.88rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background 0.2s;
}

.btn-back:hover {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.04);
}

.flex-2 {
  flex: 2;
  margin-top: 0;
}

/* Waiver step */
.waiver-intro {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 16px;
}

.waiver-text {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.waiver-text p {
  font-family: 'HND', sans-serif;
  font-size: 0.82rem;
  color: var(--text);
  line-height: 1.75;
}

.waiver-text strong {
  color: var(--white);
}

.waiver-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
}

.waiver-checkbox input[type='checkbox'] {
  margin-top: 3px;
  accent-color: var(--pink);
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.waiver-checkbox label {
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
  color: var(--text);
  cursor: pointer;
}

.form-error {
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
  color: #ff4d6a;
  text-align: center;
  margin-bottom: 8px;
}

/* Payment step */
.payment-step {
  text-align: center;
  padding: 20px 0;
}

.payment-summary {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
}

.payment-label {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: var(--pink-bright);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.payment-event {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 900;
  font-size: 1.1rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 4px;
}

.payment-amount {
  font-family: 'HND', system-ui, sans-serif;
  font-weight: 950;
  font-size: 2.4rem;
  color: var(--white);
  margin-bottom: 4px;
}

.payment-detail {
  font-family: 'HND', sans-serif;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
}

.checkout-info {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkout-info p {
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.secure-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.secure-badge span {
  font-family: 'HND', sans-serif;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.3);
}

/* Promo code */
.promo-section {
  margin-top: 16px;
}

.promo-row {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.promo-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--white);
  font-family: 'HND', sans-serif;
  font-size: 0.88rem;
  outline: none;
}

.promo-btn {
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-family: 'HND', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}

.promo-success {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: #4caf50;
  margin-bottom: 4px;
}

.promo-error {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: var(--pink-bright);
  margin-bottom: 4px;
}

.promo-hint {
  font-family: 'HND', sans-serif;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
}

.promo-hint strong {
  color: rgba(255, 255, 255, 0.6);
}

/* Add-on services */
.addons-optional {
  font-weight: 400;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
}

.addons-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.addon-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.3s;
}

.addon-item:hover {
  border-color: rgba(186, 50, 139, 0.4);
}

.addon-item input[type='checkbox'] {
  margin-top: 3px;
  accent-color: var(--pink);
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.addon-title {
  font-family: 'HNDC', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--white);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.addon-price {
  color: var(--pink-bright);
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
}

.addon-desc {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 6px;
  line-height: 1.5;
}

/* Mobile */
@media (max-width: 768px) {
  .reg-body {
    padding: 24px 20px;
  }

  .reg-body h2 {
    font-size: 1.4rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-group {
    margin-bottom: 14px;
  }

  .step-buttons {
    flex-direction: column;
  }

  .flex-2 {
    margin-top: 0;
  }
}
</style>
